import React from 'react';
import Dropdown from '@common/components/dropdown/dropdown';
import Icon from '@common/components/icon';
import ConfirmButton from '@common/components/confirm-button';
import { QuestionIcon } from '@modules/performance/components/question-icon/question-icon';
import { PerformanceQuestionType } from '@modules/performance/definitions';
import { useTranslation } from 'react-i18next';
import { PerformanceFormComponent } from '@modules/performance/types';
import List from '@common/components/list/pure';
import { combineClassNames } from '@common/utils/combineClassNames';
import { Button } from '@common/components/button';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import dndManager from '@common/utils/dnd-manager';
import { ObjectValues } from '@common/types/util-types';
import { QuestionDropdown } from '@modules/performance/components/question-dropdown/question-dropdown';

type QuestionItemOwnProps = {
  item: PerformanceFormComponent;
  componentId: string;
  onOpen: (id: string) => void;
  onDrag: (sourceId: string, targetIndex: number) => void;
  onCommit?: () => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
};

const QuestionItem = ({
  item,
  componentId,
  onOpen,
  onDrag,
  onCommit,
  onDelete,
  onDuplicate,
}: QuestionItemOwnProps) => {
  const { t } = useTranslation();
  const rootRef = React.useRef(null);
  const selected = item.id === componentId;

  const [, dropRef] = useDrop({
    accept: ['component'],
    hover: (source: { id: string }) => {
      if (source.id === item.id) return;

      onDrag(source.id, item.index);
    },
  });

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    type: 'component',
    item: () => ({ id: item.id }),
    isDragging: (monitor) => item.id === monitor.getItem().id,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: () => {
      if (onCommit) onCommit();
    },
  });

  return (
    <div
      // @ts-expect-error
      ref={dropRef(previewRef(rootRef))}
      className={combineClassNames(
        'tw-flex tw-flex-row tw-items-center tw-gap-3 tw-p-3 tw-bg-gray-50 tw-rounded-lg',
        selected
          ? 'tw-border tw-border-solid tw-border-[var(--color-button)] tw-font-semibold tw-cursor-default'
          : 'tw-cursor-pointer'
      )}
      onClick={() => {
        if (!selected) onOpen(item.id);
      }}
      style={
        ((!isDragging || selected) && { opacity: isDragging ? 0 : 1 }) ||
        undefined
      }
    >
      <div
        ref={(!isDragging && dragRef) || undefined}
        className="tw-flex tw-items-center tw-gap-2"
      >
        <Icon
          type="dragable_six_dots"
          className="tw-cursor-move tw-text-gray-textGray !tw-h-4 !tw-w-4"
        />
        <QuestionIcon type={item?.type} />
      </div>
      <div
        className="tw-flex-[1_1_auto] tw-text-base tw-font-medium
      tw-text-gray-textDark tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap"
      >
        {item?.settings?.text ||
          t('performance:question_type_title', {
            context: item?.type,
          })}
      </div>
      <Dropdown
        alignRight
        id="survey"
        toggle={<Icon type="more_horiz" className="tw-text-gray-muted" />}
      >
        {item?.type !== PerformanceQuestionType.COMPETENCIES && (
          <>
            <Dropdown.Item onClick={() => onDuplicate(item.id)}>
              {t('performance:duplicate')}
            </Dropdown.Item>
            <Dropdown.Divider />
          </>
        )}
        {onDelete && (
          <ConfirmButton
            title={t('performance:delete_question_title')}
            description={t('performance:delete_question_description')}
            onConfirm={() => onDelete(item.id)}
          >
            <Dropdown.Item danger>{t('performance:delete')}</Dropdown.Item>
          </ConfirmButton>
        )}
      </Dropdown>
    </div>
  );
};

type QuestionsListProps = {
  components: PerformanceFormComponent[];
  componentId: string;
  addQuestion: (type: ObjectValues<typeof PerformanceQuestionType>) => void;
  openQuestion: QuestionItemOwnProps['onOpen'];
  handleDrag: (screenId: string, order: string[]) => void;
  onReorderCommit?: (order: string[]) => void;
  deleteQuestion: QuestionItemOwnProps['onDelete'];
  duplicateQuestion: QuestionItemOwnProps['onDuplicate'];
};

export const QuestionsList = ({
  components = [],
  componentId,
  openQuestion,
  addQuestion,
  handleDrag,
  onReorderCommit,
  deleteQuestion,
  duplicateQuestion,
}: QuestionsListProps) => {
  const { t } = useTranslation();
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const scrollPositionRef = React.useRef<number>(0);
  const previousComponentIdRef = React.useRef<string | undefined>(componentId);

  const getCurrentOrder = React.useCallback(
    () => [...components]
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
        .map((c) => c.id),
    [components]
  );

  React.useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      scrollPositionRef.current = scrollContainer.scrollTop;
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, []);

  React.useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    if (previousComponentIdRef.current !== componentId && previousComponentIdRef.current !== undefined) {
      requestAnimationFrame(() => {
        if (scrollContainer) {
          scrollContainer.scrollTop = scrollPositionRef.current;
        }
      });
    }
    previousComponentIdRef.current = componentId;
  }, [componentId]);

  return (
    <DndProvider manager={dndManager}>
      <div className="tw-flex tw-flex-row tw-justify-between tw-items-center">
        <div className="tw-text-xs tw-font-semibold tw-text-gray-muted tw-uppercase">
          {t('performance:form_order')}
        </div>
        <QuestionDropdown
          toggle={
            <Button type="transparent" size="small" icon="add">
              {t('performance:add_block')}
            </Button>
          }
          enabledComponents={[
            PerformanceQuestionType.OPEN_QUESTION,
            PerformanceQuestionType.COMPETENCIES,
            PerformanceQuestionType.CONTENT_MODULE,
            PerformanceQuestionType.TALENT_REVIEW
          ]}
          disabledComponents={
            components.some(
              (c) => c?.type === PerformanceQuestionType.COMPETENCIES
            )
              ? [PerformanceQuestionType.COMPETENCIES]
              : []
          }
          onSelect={(item) => addQuestion(
              item?.type as ObjectValues<typeof PerformanceQuestionType>
            )}
        />
      </div>

      <List
        containerRef={scrollContainerRef}
        containerClassName="tw-flex tw-flex-col tw-gap-2 tw-overflow-auto tw-max-h-[calc(100vh-320px)] tw-pr-1"
        items={components}
        renderRow={QuestionItem as any}
        rowProps={{
          componentId,
          onOpen: openQuestion,
          onDrag: handleDrag,
          onCommit: () => onReorderCommit && onReorderCommit(getCurrentOrder()),
          onDelete: deleteQuestion,
          onDuplicate: duplicateQuestion,
        }}
      />
    </DndProvider>
  );
};
